/*
 * File: index.ts
 * File Created: 2023-12-18 11:47:08
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-18 07:38:32
 * Modified By: Val Liu
 * -----
 */

import { Vue } from "vue-property-decorator";
import { MetaAttribute, MetaParser } from "@root/core";
import { IRegistryMetaBase, Registry } from "@root/core/utilities/registry";
import { ITableBodyCellAttr } from "@root/components/table/core/lib/core";
import { IFormComponentAttr } from "@root/components/form/core/lib/core";
import { IServer } from "@root/components/interfaces";

/// interfaces
export interface IUITagInput {
    /// the target current applying (table / form)
    target: EUITagTarget;

    /// the rule string after @ui*
    /// can also be function
    rule: string | { (o: IUITagResult["props"]): any } | null;

    /// pass in IUITagResult
    inputs: IUITagResult;

    /// handle decorators related to other meta, ex: @uiColumnGroup
    metas: MetaParser;
    metaIndex: number;
    meta: MetaAttribute;

    /// default initial
    // if @UITag.set({
    //     default: true,
    // }), triggered, the `isDefault` will be true.
    isDefault: boolean;

    /// other related variables
    variables: IUITagVariables;

    /// the component this decorator belong to
    component: Vue | undefined;
}

interface IUITagParameterMeta {
    meta?: any;
}
export type IUITagParameter = (
    | Partial<ITableBodyCellAttr>
    | Partial<IFormComponentAttr>
) &
    IUITagParameterMeta &
    Record<string, any>;

export interface IUITagVariables {
    /// server: the fetching source
    server: IServer | undefined;
    /// table: the Table
    /// form: the Form
    hostElement: Vue | undefined;
    /// table: the TableHeaderCell | TableCell
    /// form: the FormElement
    element: Vue | undefined;
}

export interface IUITagResult {
    /// modify props
    props?: IUITagParameter;
    /// modify styles
    styles?: any;
    /// special rules that should be taken care of. ex: wrap / search
    specials?: Partial<IUISubmitWrapSpecial> &
        Partial<IUISearchSpecial> &
        Partial<IUISubmitIgnoreSpecial> &
        Partial<IUISubmitConverterSpecial> &
        Partial<IUIRelationConnectSpecial> &
        Partial<IUIRelationPairSpecial> &
        Partial<IUITagMoreTags> &
        Partial<IUICRUDTypeSpecial>;
}

export enum EUITagTarget {
    TableSearch,
    TableHeaderCellContainer,
    TableHeaderCell,
    TableBodyCellContainer,
    TableBodyCell,
    TableRow,

    FormElement,
    FormSubmit,
}

/// EUITagRuleType[] represent an array, which holds one or multiple EUITagRuleType
export type EUITagRuleUnion = EUITagRuleType[] | EUITagRuleType;
export enum EUITagRuleType {
    /// parse into anything allowed with eval
    Eval,
    /// parse into js object
    Object,
    /// parse into date
    Date,
    /// parse into string
    String,
    /// parse into number
    Number,
    /// parse into boolean
    Boolean,
    /// parse into lambda or scoped function. return the function
    Function,
    /// parse into meta defined type. used by @uiDefault which has runtime type.
    Meta,
    /// parse it with my own rule
    Custom,
}

export interface IUITag {
    allow?: EUITagTarget[];
    ruleTypes?: EUITagRuleUnion[];
    parse(input: IUITagInput): IUITagResult | null;
}

/// If the default is true, the decorator generate default value,
/// even if this decorator is not used.
/// with this case, input.rule is undefined.
export interface IUITagMeta extends IRegistryMetaBase {
    default?: boolean;
}

/// interface for UITag.Default
export type IUITagDefaultMeta = Record<string, any>;

/// registry
const { registerClass: set, getInstance: get } = Registry.create<IUITagMeta>(
    Symbol("UITag"),
);
const { registerClass: setDefault, getClass: getDefault } =
    Registry.create<IUITagDefaultMeta>(Symbol("UITagDefault"));
export const UITag = { set, get, setDefault, getDefault };

/// specials
interface IUISubmitWrapSpecial {
    submitWrap: string | null;
}
export interface IUISearchSpecial {
    search: {
        [index: string]:
            | ["string", true | "equal" | "like"]
            | ["Date", true]
            | ["boolean", true | "equal"]
            | ["number", true]
            | ["enum", any]
            | ["any", true | "equal"];
    };
}
/// UISubmitIgnore
interface IUISubmitIgnoreSpecial {
    submitIgnore: boolean;
}

/// UISubmitConverter
interface IUISubmitConverterSpecial {
    submitConverter: (o: IFormComponentAttr) => any;
}

/// uiRelationConnect
interface IUIRelationConnectSpecial {
    relationConnect: boolean;
}

/// UIToggle
export interface IUIToggle {
    /// Is it the enable part of the pair?
    state: boolean;
}

/// UIRelationPair
export type IUIRelationPair = IUIToggle;
interface IUIRelationPairSpecial {
    relationPair: IUIRelationPair;
}

/// MoreTags
export interface IUITagMoreTagsUnit {
    tag: string;
    rule: any;
}
interface IUITagMoreTags {
    moreTags: IUITagMoreTagsUnit[];
}

/// CRUDType
export interface IUICRUDTypeSpecial {
    CRUDType: {
        R?: string | MetaParser | null;
        C?: string | MetaParser | null;
        U?: string | MetaParser | null;
        D?: string | MetaParser | null;
    };
}
